import React from 'react';
import './ArrivalFlowBadge.css';

/**
 * Landing interval + runway wind, beside "Active Rwy" on the Predictions page.
 *
 * The backend (ArrivalFlow, getPredictionsForDate.arrivalFlow, today only) sends:
 *   state            NONE (too little traffic: "-") | NORMAL | REDUCED (slower than usual, or a
 *                    stoppage, while aircraft are waiting)
 *   intervalMinutes  p25 of the last 30 min's landing gaps; landings / goArounds in that window
 *   windDirDeg / windKt / gustKt, windRelativeDeg (to the runway, + from the right),
 *   headwindKt / crosswindKt / gustCrosswindKt, windLevel CALM|NORMAL|STRONG|SEVERE, gustBars 0-5
 *
 * Every state carries words or a shape as well as colour, so the Mono theme loses nothing.
 */

const pad3 = (n) => String(Math.round(n) % 360 || 360).padStart(3, '0');

/** "200/22G36", "VRB/04", "CALM". Null with no METAR. */
export function windText(af) {
  if (!af || af.windKt == null) return null;
  if (af.windKt === 0) return 'CALM';
  const dir = af.windVariable || af.windDirDeg == null ? 'VRB' : pad3(af.windDirDeg);
  return `${dir}/${String(af.windKt).padStart(2, '0')}${af.gustKt ? `G${af.gustKt}` : ''}kt`;
}

/** Pure: what the badge shows. Null when the backend sent nothing (past dates, errors). */
export function flowView(af) {
  if (!af) return null;
  const reduced = af.state === 'REDUCED';
  let interval = '-';
  if (af.intervalMinutes != null) interval = `${af.intervalMinutes.toFixed(1)}m`;
  else if (reduced) interval = `${af.landings} in 30m`;

  const tip = [];
  if (af.state === 'NONE') {
    tip.push('Landing interval: not enough traffic in the last 30 min to judge the flow.');
  } else {
    tip.push(`Landing interval ${af.intervalMinutes != null ? `${af.intervalMinutes.toFixed(1)} min` : 'n/a'}`
      + ' (the tightest quarter of gaps between landings in the last 30 min)');
    if (af.baselineMinutes != null) tip.push(`Usual on this runway: ${af.baselineMinutes.toFixed(1)} min`);
    if (reduced) tip.push('REDUCED: landing slower than usual, or stopped, while aircraft are waiting');
  }
  tip.push(`${af.landings} landed, ${af.goArounds} go-around${af.goArounds === 1 ? '' : 's'} in 30 min;`
    + ` ${af.inboundSoon} due in 30 min${af.holding ? `, ${af.holding} holding` : ''}`);
  if (af.longestGapMinutes != null && af.longestGapMinutes >= 8) {
    tip.push(`Longest gap ${af.longestGapMinutes.toFixed(0)} min (runway change or stoppage)`);
  }

  const wind = windText(af);
  if (wind) {
    const parts = [`Wind ${wind}`];
    if (af.runway && af.crosswindKt != null) {
      const side = af.windRelativeDeg > 0 ? 'from the right' : af.windRelativeDeg < 0 ? 'from the left' : '';
      parts.push(`on ${af.runway}: crosswind ${Math.round(af.crosswindKt)} kt`
        + (af.gustCrosswindKt != null ? ` (gusts ${Math.round(af.gustCrosswindKt)})` : '')
        + (side ? ` ${side}` : '')
        + `, ${af.headwindKt >= 0 ? 'headwind' : 'tailwind'} ${Math.abs(Math.round(af.headwindKt))} kt`);
    }
    if (af.windLevel === 'SEVERE') parts.push('Strong crosswind');
    else if (af.windLevel === 'STRONG') parts.push(af.headwindKt != null && af.headwindKt <= -5 ? 'Tailwind' : 'Crosswind');
    if (af.gustBars) parts.push(`Gustiness ${af.gustBars}/5 (gusts ${af.gustKt - af.windKt} kt above the mean)`);
    tip.push(parts.join('. '));
    if (af.metarRaw) tip.push(af.metarRaw);
  }

  return {
    reduced,
    interval,
    goArounds: af.goArounds || 0,
    wind,
    windLevel: (af.windLevel || 'NORMAL').toLowerCase(),
    gustBars: af.gustBars || 0,
    title: tip.join('\n'),
  };
}

/**
 * Wind arrow against the runway. The runway is drawn vertical, landing direction up; the arrow
 * shows where the wind blows to, so a headwind points down the runway at the aircraft. Without a
 * runway it is drawn against true north instead. Longer arrow = stronger wind.
 */
export function WindArrow({ af }) {
  if (!af || af.windKt == null || af.windKt === 0 || af.windVariable || af.windDirDeg == null) return null;
  const relative = af.windRelativeDeg != null;
  const rot = ((relative ? af.windRelativeDeg : af.windDirDeg) + 180) % 360;
  const len = 4 + Math.min(af.gustKt || af.windKt, 40) / 40 * 5;   // 4..9 px from the centre
  return (
    <svg className="af-arrow" width="20" height="20" viewBox="-10 -10 20 20" aria-hidden="true">
      {relative && <rect className="af-runway" x="-1.5" y="-9" width="3" height="18" rx="1" />}
      <g transform={`rotate(${rot})`}>
        <line x1="0" y1={len} x2="0" y2={-len} />
        <path d={`M -3 ${-len + 3.5} L 0 ${-len} L 3 ${-len + 3.5}`} />
      </g>
    </svg>
  );
}

/** Five rising bars, filled to the gustiness, like a signal-strength meter. */
export function GustBars({ bars }) {
  return (
    <svg className="af-gust" width="17" height="12" viewBox="0 0 17 12" aria-hidden="true">
      {[0, 1, 2, 3, 4].map((i) => (
        <rect key={i} x={i * 3.5} y={10 - i * 2} width="2.5" height={2 + i * 2}
          className={i < bars ? 'on' : 'off'} />
      ))}
    </svg>
  );
}

export default function ArrivalFlowBadge({ arrivalFlow }) {
  const v = flowView(arrivalFlow);
  if (!v) return null;
  return (
    <span className={`arrival-flow-badge${v.reduced ? ' reduced' : ''}`} title={v.title}>
      <span className="af-interval">Interval <strong>{v.interval}</strong>{v.reduced && <span className="af-flag">▼ reduced</span>}</span>
      {v.goArounds > 0 && <span className="af-ga">↺ {v.goArounds} GA</span>}
      {v.wind && (
        <span className={`af-wind wind-${v.windLevel}`}>
          <WindArrow af={arrivalFlow} />
          <span className="af-wind-text">{v.wind}</span>
          <GustBars bars={v.gustBars} />
        </span>
      )}
    </span>
  );
}
